import { reconcileAfterServiceDetailsSave, ServiceDetailsConflict, type ServiceDetailsBase } from './billing/serviceDetailsConcurrency';
import { serviceDetailsPayload, type ServiceDetailsPart } from './billing/billingLineSource';
/**
 * ServiceDetailContent Component
 *
 * Inline content for editing booking-level service details (labor items, parts, extras).
 * Also displays and allows changing the assigned mechanic for this service.
 * Replaces the former ServiceDetailDialog — renders directly inside an ExpandableCard.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useDebounce } from '@/hooks/useDebounce';
import { LaborItemRow, persistedIdPattern } from './LaborItemRow';
import {
  applyAdoptedLaborItem,
  isHaynesProSnapshotsEnabled,
} from './haynespro/haynesproWorkOrderTime';
import {
  useHaynesProWorkOrderTime,
  type HaynesProAdoptResult,
} from '@/hooks/useHaynesProWorkOrderTime';
import { PartCatalogInput } from './PartCatalogInput';
import { PartBundlePicker } from './PartBundlePicker';
import { PartOrderStatusSelect } from './PartOrderStatusSelect';
import { VatRatePicker } from './VatRatePicker';
import { UnitSelect } from './UnitSelect';
import { DROPDOWN_MRU_SELECTORS, useDropdownMru } from '@/hooks/useDropdownMru';
import { UserAvatar } from '@/components/common';
import { useMechanicsWithSkills } from '@/hooks/useStaffUsers';
import { useServices } from '@/hooks/useServices';
import { splitByEligibility } from '@/utils/skill-matching';
import { MechanicCertificateLock } from './create/ResourceAssignmentFields';
import { CertificateOverrideDialog } from './CertificateOverrideDialog';
import {
  certificateRequiredSkills,
  isCertificateOverrideForbiddenError,
  mechanicCertificateIssue,
  parseCertificateGateError,
  type CertificateRequiredError,
  type SkillGateOverride,
} from './certificateGateError';
import {
  useUpdateBookingServiceDetails,
  type BookingServiceLaborItem,
  type BookingServicePart,
  type BookingServiceExtra,
  type PartOrderStatus,
} from '@/hooks/useWorkOrders';
import { useNotify } from '@/hooks/useNotify';
import { describeApiError } from '@/lib/describeApiError';
import type { PartBundleExpansion } from '@/hooks/useInventory';
import {
  PART_RESOLVE_PERMISSIONS,
  asPartPriceFailure,
  partPriceFailureLabelKey,
  partPriceSourceLabelKey,
  resolvedUnitGrossCents,
  useWorkOrderPartPricing,
  type PartPriceFailureReason,
  type ResolvedPartLinePrice,
  type ResolvedPartSource,
} from '@/hooks/useWorkOrderPartPricing';
import {
  accessKey,
  fenceHolds,
  isSessionCancel,
  takeFence,
  usePricingAccess,
  type PricingFence,
} from '@/features/inventory/pricing/pricingSession';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { eurosToCents, lineTotalGrossCents, lineNetAfterDiscountCents } from '@/utils/money';
import { formatDuration, sumHours } from '@/utils/duration';
import { durationExceedsAllocatedSlot } from '@/utils/schedule-slot';
import type { VatRate } from '@/types';
import { distinctServiceTitle } from './serviceTitle';
import { isFixedPricePackage } from './fixedPricePackage';

export { applyAdoptedLaborItem } from './haynespro/haynesproWorkOrderTime';

export interface BookingServiceData {
  /** Catalog name: stays the heading (#3655). */
  name?: string;
  /** Work-specific title under the catalog name; null when none. */
  title?: string | null;
  /** Fixed-price fields: a fixed-price package locks its labor rows (#3655). */
  fixedPriceCents?: number | null;
  fixedPriceOverrideCents?: number | null;
  laborItems: BookingServiceLaborItem[];
  parts: BookingServicePart[];
  extras: BookingServiceExtra[];
  /** Technician remarks for this task (#1754) — customer-facing, shown on the invoice/print. */
  notes: string | null;
}

export interface ServiceDetailContentProps {
  bookingServiceId: string;
  detailsBase?: ServiceDetailsBase;
  /** Catalog service id — used to resolve this service's required skills. */
  serviceId: string;
  workOrderId: string;
  mechanicId: string | null;
  mechanicName: string | null;
  bookingServiceData: BookingServiceData | null;
  /** Parent service status — drives the inherited-pause display on steps (#1028). */
  serviceStatus?: string;
  /** Work order state forbids labor-item status commands (#1028). */
  statusActionsLocked?: boolean;
  onRemove: (bookingServiceId: string) => void;
  onMechanicChange: (
    bookingServiceId: string,
    mechanicId: string | null,
    options?: { skillGateOverride: SkillGateOverride },
  ) => void | Promise<void>;
  onSaved?: () => void;
  onDirtyChange?: (bookingServiceId: string, isDirty: boolean) => void;
  removing?: boolean;
  saving?: boolean;
  laborRateCents?: number | null;
  formatPrice?: (cents: number) => string;
  /** Hours already allocated to this work order (mechanic window). */
  allocatedSlotHours?: number | null;
  /** Labor hours from the other services on the same work order. */
  otherLaborHours?: number;
  vehicleId?: string;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  pricingType?: string | null;
  /** When false, labor-item hours stay visible but read-only (#2706). */
  hoursEditable?: boolean;
}

// Local labor-item draft: the server-shaped item plus a client-only key that
// survives id churn (temp id -> real id, or across autosave round-trips), so
// LaborItemRow never remounts (and loses focus) for a row the user hasn't
// actually removed (#1025).
export interface LaborItemDraft extends BookingServiceLaborItem {
  clientKey: string;
}

/**
 * Reconciles a fresh server labor-item list with the previous local drafts,
 * carrying over each row's `clientKey` so React keys stay stable across
 * refetches. Persisted rows match by id. Brand-new rows (temp id -> real id,
 * the client's first save of that row) have no id to match on, so they're
 * matched positionally against the previous not-yet-persisted rows — safe
 * because labor items are only ever appended, never reordered (#1025).
 */
export function mergeLaborItemDrafts(
  prevItems: LaborItemDraft[],
  serverItems: BookingServiceLaborItem[],
): LaborItemDraft[] {
  const prevByPersistedId = new Map(
    prevItems.filter((p) => persistedIdPattern.test(p.id)).map((p) => [p.id, p])
  );
  const leftoverPrev = prevItems.filter((p) => !persistedIdPattern.test(p.id));
  let leftoverIndex = 0;
  return serverItems.map((server) => {
    const matched = prevByPersistedId.get(server.id);
    if (matched) return { ...server, clientKey: matched.clientKey };
    const fallback = leftoverPrev[leftoverIndex++];
    return { ...server, clientKey: fallback?.clientKey ?? crypto.randomUUID() };
  });
}

/**
 * Builds the wire payload for the bulk details save. Recomputes `sortOrder`
 * from each array's current index rather than trusting the stale
 * `sortOrder` field — removing an item leaves the survivors' `sortOrder`
 * non-contiguous, and a later add would otherwise reuse an already-taken
 * value (which in turn would break `mergeLaborItemDrafts`'s positional
 * fallback matching for brand-new rows) (#1025). Keeps local ids for
 * reconciliation; the shared writer strips temporary labor and part ids and all
 * extra ids from the wire payload before the backend saves them (#3566).
 * Blank part and extra rows stay in the draft and are left out of the payload
 * so they do not fail the save (#4125). A labour row with no description is
 * still sent.
 */
function isBlankPartDraft(part: { name: string; partId?: string | null; partCategoryId?: string | null }): boolean {
  return part.name.trim() === '' && !part.partId && !part.partCategoryId;
}

function isBlankExtraDraft(extra: { description: string }): boolean {
  return extra.description.trim() === '';
}

/** Puts rows the payload omitted (blank parts/extras) back in their local places. */
function restoreOmittedBlankRows<T>(localRows: readonly T[], mergedRows: readonly T[], isBlank: (row: T) => boolean): T[] {
  const restored: T[] = [];
  let mergedIndex = 0;
  for (const row of localRows) {
    if (isBlank(row)) {
      restored.push(row);
      continue;
    }
    const saved = mergedRows[mergedIndex];
    if (saved === undefined) {
      restored.push(row);
      continue;
    }
    mergedIndex += 1;
    restored.push(saved);
  }
  for (; mergedIndex < mergedRows.length; mergedIndex += 1) {
    const saved = mergedRows[mergedIndex];
    if (saved !== undefined) restored.push(saved);
  }
  return restored;
}

function toDetailsPayload(
  laborItems: LaborItemDraft[],
  parts: PartDraft[],
  extras: ExtraDraft[],
  notes: string | null,
  title: string,
  laborLocked: boolean,
) {
  const sendableParts = parts.filter((part) => !isBlankPartDraft(part));
  const sendableExtras = extras.filter((extra) => !isBlankExtraDraft(extra));
  return {
    title: title.trim() || null,
    notes,
    // A fixed-price package's labor rows are locked (#3655): never sent.
    ...(laborLocked ? {} : {
      laborItems: laborItems.map((item, index) => ({
        id: item.id,
        description: item.description,
        estimatedHours: item.estimatedHours,
        discountPercent: item.discountPercent,
        sortOrder: index,
      })),
    }),
    parts: sendableParts.map((p, index) => ({ ...p, sortOrder: index })),
    extras: sendableExtras.map((e, index) => ({ ...e, sortOrder: index })),
  };
}

interface PartDraft {
  id?: string;
  partCategoryId: string | null;
  /** Inventory part the row is filled with; null for an unfilled category slot or a hand-typed row (#3842). */
  partId: string | null;
  name: string;
  isRequired: boolean;
  quantity: number;
  /** Exact gross (VAT-included) price the user typed, in whole cents (#1022). */
  unitPriceGrossCents: number;
  vatPercent: number;
  discountPercent: number;
  notes: string | null;
  sortOrder: number;
}

/** The catalog part a user picked for a row: only what the row needs from the part list (never its price). */
interface PickedPart {
  id: string;
  name: string;
  categoryId: string;
}

/**
 * Transient price-choice state of one part row, keyed by the row's draft id. It is never persisted and never on the wire: the
 * saved line carries only the catalog part it is filled with (`partId`, #3842), not where its price came from, so a reload
 * shows no source.
 *  - `pending`: a pick whose price is being resolved. The row is NOT changed yet (its name and price stay as the user left
 *    them) and the draft is not saved until the pick settles.
 *  - `failed`: the price could not be resolved; the pick was not applied and the row keeps what it had.
 *  Both carry the `fence` (owner, session generation, permissions) the pick was started under, or null when it was refused
 *  before anything was sent: an answer, an application or a retry under any other owner is void.
 *  - `resolved`: the row's price is the server-resolved price of `partId`, stored as `grossCents`. It is shown only while
 *    the row's price still is that amount.
 *  - `manual`: the user changed the price of a resolved row.
 */
type PartRowPricing =
  | { kind: 'pending'; part: PickedPart; workOrderId: string; fence: PricingFence | null }
  | { kind: 'failed'; part: PickedPart; reason: PartPriceFailureReason; fence: PricingFence | null }
  | { kind: 'resolved'; partId: string; source: ResolvedPartSource; grossCents: number }
  | { kind: 'manual'; partId: string };

type RowPricingMap = Record<string, PartRowPricing>;

const NO_ROW_PRICING: RowPricingMap = {};

/**
 * Carries the price-choice state of rows across a save: a row whose temporary id the server replaced follows its proven new id
 * (`idMap`, submitted id -> committed id); a row that is no longer in the draft loses its state, so a late result for it is
 * dropped.
 */
export function remapRowPricing(
  current: RowPricingMap,
  idMap: Record<string, string> | undefined,
  rows: readonly { id?: string | undefined }[],
): RowPricingMap {
  const present = new Set(rows.map((row) => row.id));
  const next: RowPricingMap = {};
  for (const [key, entry] of Object.entries(current)) {
    const mapped = idMap?.[key] ?? key;
    if (present.has(mapped)) next[mapped] = entry;
  }
  return next;
}

const hasPendingPick = (map: RowPricingMap): boolean => Object.values(map).some((entry) => entry.kind === 'pending');

/** The payload leaves `partId` optional; a draft always carries it (#3842). */
function toPartDrafts(rows: ServiceDetailsPart[] | undefined): PartDraft[] {
  return (rows ?? []).map((row) => ({ ...row, partId: row.partId ?? null }));
}

interface ExtraDraft {
  id?: string;
  description: string;
  amountGrossCents: number;
  isTaxable: boolean;
  hasVariableQuantity: boolean;
  unit: string | null;
  quantity: number | null;
  sortOrder: number;
}

/** Default Finnish VAT rates used when tenant has none configured */
const defaultVatRates: VatRate[] = [
  { id: 'fi-25.5', label: '25,5 %', percentage: 25.5 },
  { id: 'fi-14', label: '14 %', percentage: 14 },
  { id: 'fi-10', label: '10 %', percentage: 10 },
  { id: 'fi-0', label: '0 %', percentage: 0 },
];

/** Plain Enter on a product-row text field adds a row; IME, Shift+Enter, and open list popups are left alone (#2674). */
function shouldAddProductRowOnEnter(event: KeyboardEvent): boolean {
  if (event.key !== 'Enter' || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey || event.repeat) {
    return false;
  }
  if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) {
    return false;
  }
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return false;
  if (target.getAttribute('aria-expanded') === 'true') return false;
  if (target.closest('[role="listbox"], [role="menu"], [role="combobox"][aria-expanded="true"]')) {
    return false;
  }
  return true;
}

/** What a part row says about its price choice: waiting, failed (with a way out), or the source of the price it holds. */
function PartPriceNote({
  entry,
  grossCents,
  onRetry,
  onDismiss,
}: {
  entry: PartRowPricing | undefined;
  grossCents: number;
  onRetry: (part: PickedPart) => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation('workorders');
  if (!entry) return null;
  if (entry.kind === 'pending') {
    return (
      <Box sx={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }} role="status">
        <CircularProgress size={12} aria-label={t('detail.partPricing.pending', { name: entry.part.name })} />
        <Typography variant="caption">{t('detail.partPricing.pending', { name: entry.part.name })}</Typography>
      </Box>
    );
  }
  if (entry.kind === 'failed') {
    return (
      <Alert
        severity="warning"
        sx={{ flexBasis: '100%', py: 0 }}
        action={
          <>
            <Button color="inherit" size="small" onClick={() => onRetry(entry.part)}>
              {t('detail.partPricing.retry')}
            </Button>
            <Button color="inherit" size="small" onClick={onDismiss}>
              {t('detail.partPricing.dismiss')}
            </Button>
          </>
        }
      >
        {t('detail.partPricing.notApplied')} {t(partPriceFailureLabelKey(entry.reason))}
      </Alert>
    );
  }
  // A resolved price is only claimed while the row still holds that amount (a remote edit or a merge may have changed it).
  if (entry.kind === 'resolved' && entry.grossCents !== grossCents) return null;
  const source = entry.kind === 'resolved' ? entry.source : 'manual';
  return (
    <Typography variant="caption" color="text.secondary" sx={{ flexBasis: '100%' }} data-testid="part-price-source" data-source={source}>
      {t('detail.partPricing.sourceLabel')}: <strong>{t(partPriceSourceLabelKey(source))}</strong>
    </Typography>
  );
}

export function ServiceDetailContent({
  bookingServiceId,
  detailsBase,
  serviceId,
  workOrderId,
  mechanicId,
  mechanicName,
  bookingServiceData,
  serviceStatus,
  statusActionsLocked = false,
  onRemove,
  onMechanicChange,
  onSaved,
  onDirtyChange,
  removing = false,
  saving = false,
  laborRateCents,
  formatPrice,
  allocatedSlotHours = null,
  otherLaborHours = 0,
  vehicleId,
  vehicleMake,
  vehicleModel,
  pricingType,
  hoursEditable = true,
}: ServiceDetailContentProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  // A fixed-price package's labor rows are locked; extra work is a separate service (#3655).
  const laborLocked = isFixedPricePackage({
    pricingType,
    fixedPriceCents: bookingServiceData?.fixedPriceCents,
    fixedPriceOverrideCents: bookingServiceData?.fixedPriceOverrideCents,
  });
  const laborEditable = hoursEditable && !laborLocked;
  const { data: mechanics = [] } = useMechanicsWithSkills();
  const { data: allServices = [] } = useServices({ isActive: true, sortBy: 'usage' });
  const mechanicMru = useDropdownMru(DROPDOWN_MRU_SELECTORS.mechanic);
  const updateDetails = useUpdateBookingServiceDetails(bookingServiceId);
  const { capabilityQuery: haynesProCapabilityQuery } = useHaynesProWorkOrderTime(workOrderId);
  const haynesProEnabled = isHaynesProSnapshotsEnabled(haynesProCapabilityQuery.data);
  const notify = useNotify();
  const { tenantSettings } = useTenantSettings();
  const pricing = useWorkOrderPartPricing(workOrderId);
  const pricingAccess = usePricingAccess();
  const workOrderIdRef = useRef(workOrderId);
  workOrderIdRef.current = workOrderId;
  // Price-choice state per part row (see PartRowPricing). The ref is the synchronous truth for late results and the
  // autosave gate; the state only re-renders. It belongs to ONE owner of ONE work order: a different tenant, user, session
  // family or generation, a lock, a change of the rights pricing needs, or another work order replaces it with an empty
  // state in the very render that sees the change, so no frame shows (and no retry or queued pick can use) the previous
  // owner's sources, failures or part names.
  const pricingScope = `${accessKey(pricingAccess)}|${pricing.canResolve ? 'resolve' : 'no-resolve'}|${workOrderId}`;
  const pricingScopeRef = useRef(pricingScope);
  const pricingRef = useRef<RowPricingMap>({});
  if (pricingScopeRef.current !== pricingScope) {
    pricingScopeRef.current = pricingScope;
    pricingRef.current = {};
  }
  const [pricingState, setPricingState] = useState<{ scope: string; map: RowPricingMap }>({ scope: pricingScope, map: NO_ROW_PRICING });
  const [resolvedPicks, setResolvedPicks] = useState<{ entry: PartRowPricing; price: ResolvedPartLinePrice }[]>([]);
  if (pricingState.scope !== pricingScope) {
    setPricingState({ scope: pricingScope, map: NO_ROW_PRICING });
    setResolvedPicks([]);
  }
  const rowPricing = pricingState.scope === pricingScope ? pricingState.map : NO_ROW_PRICING;
  const writePricing = useCallback((update: (current: RowPricingMap) => RowPricingMap) => {
    pricingRef.current = update(pricingRef.current);
    setPricingState({ scope: pricingScopeRef.current, map: pricingRef.current });
  }, []);
  const pricingBusy = hasPendingPick(rowPricing);

  // Resolve VAT rates from tenant settings or use defaults
  const vatRates: VatRate[] =
    (tenantSettings as Record<string, unknown> | undefined)?.vatRates as VatRate[] | undefined
    ?? defaultVatRates;
  const defaultVatRateId =
    (tenantSettings as Record<string, unknown> | undefined)?.defaultVatRateId as string | null | undefined
    ?? null;
  const defaultVatPercent = vatRates.find((r) => r.id === defaultVatRateId)?.percentage ?? vatRates[0]?.percentage ?? 25.5;

  const baseRef = useRef(detailsBase);
  const savingRef = useRef(false);
  const unmountedRef = useRef(false);
  const [localSaveError, setLocalSaveError] = useState(false);
  const [saveErrorText, setSaveErrorText] = useState<string | null>(null);
  const [conflictBlocked, setConflictBlocked] = useState(false);
  const conflictBlockedRef = useRef(false);
  const [reloadGeneration, setReloadGeneration] = useState(0);
  const incomingBaseRef = useRef(detailsBase);
  incomingBaseRef.current = detailsBase;
  const incomingToken = detailsBase?.updatedAt;
  const [isDirty, setIsDirty] = useState(false);
  const haynesProBlocked = isDirty || updateDetails.isPending || saving;
  // Mirror of isDirty for use inside effects that must NOT re-run when it
  // changes (init hydration guard, autosave trigger, unmount flush).
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  // Blank part/extra rows are omitted from the wire payload (#4125). The ref keeps them
  // on screen after a successful save of everything else, without leaving the form dirty.
  const unsentBlankRowsRef = useRef(false);
  const lastSavedPayloadRef = useRef<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedMechanicId, setSelectedMechanicId] = useState<string>(mechanicId ?? '');
  const [certificateGate, setCertificateGate] = useState<CertificateRequiredError | null>(null);
  const [pendingMechanic, setPendingMechanic] = useState<{ value: string; previous: string } | null>(null);
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);

  // Skill-aware mechanic selection (epic #1140 / #1133). Advisory, not blocking:
  // an unqualified mechanic can still be picked, but is grouped separately and
  // triggers a warning.
  const requiredSkills = useMemo(
    () => allServices.find((s) => s.id === serviceId)?.skills.filter((sk) => sk.isRequired) ?? [],
    [allServices, serviceId],
  );
  const requiredCertificateSkills = useMemo(
    () => certificateRequiredSkills(requiredSkills),
    [requiredSkills],
  );
  const hasSkillRequirements = requiredSkills.length > 0;
  const { eligible: eligibleMechanics, ineligible: ineligibleMechanics } = useMemo(
    () => splitByEligibility(mechanics, requiredSkills),
    [mechanics, requiredSkills],
  );
  const eligibleIds = useMemo(
    () => new Set(eligibleMechanics.map((m) => m.id)),
    [eligibleMechanics],
  );
  const selectedIsIneligible =
    hasSkillRequirements && selectedMechanicId !== '' && !eligibleIds.has(selectedMechanicId);

  const [laborItems, setLaborItems] = useState<LaborItemDraft[]>([]);
  const [parts, setParts] = useState<PartDraft[]>([]);
  const [extras, setExtras] = useState<ExtraDraft[]>([]);
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState<string | null>(null);

  // Text inputs for gross price per part
  const [priceInputs, setPriceInputs] = useState<string[]>([]);
  // Text inputs for part quantities (supports decimals and comma separator)
  const [quantityInputs, setQuantityInputs] = useState<string[]>([]);
  // Text inputs for extra amounts in euros
  const [extraPriceInputs, setExtraPriceInputs] = useState<string[]>([]);
  // Text inputs for extra quantities (variable extras)
  const [extraQuantityInputs, setExtraQuantityInputs] = useState<string[]>([]);
  const partNameInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const focusNewPartNameRef = useRef(false);

  useLayoutEffect(() => {
    if (!focusNewPartNameRef.current) return;
    focusNewPartNameRef.current = false;
    partNameInputRefs.current[parts.length - 1]?.focus();
  }, [parts.length]);

  // Same for labor steps (#3276): Enter on a step row adds the next step and
  // lands in its description, so a mechanic can type a list of steps straight
  // through without reaching for the plus button.
  const laborDescriptionInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const focusNewLaborDescriptionRef = useRef(false);

  useLayoutEffect(() => {
    if (!focusNewLaborDescriptionRef.current) return;
    focusNewLaborDescriptionRef.current = false;
    laborDescriptionInputRefs.current[laborItems.length - 1]?.focus();
  }, [laborItems.length]);

  // Notify parent when dirty state changes — key by bookingServiceId so duplicate
  // catalog services (#418) get independent dirty tracking per row.
  useEffect(() => {
    onDirtyChange?.(bookingServiceId, isDirty);
  }, [isDirty, bookingServiceId, onDirtyChange]);

  useEffect(() => {
    return () => {
      onDirtyChange?.(bookingServiceId, false);
    };
  }, [bookingServiceId, onDirtyChange]);

  useEffect(() => {
    // Don't re-hydrate from the server while the user has unsaved edits — an
    // autosave invalidates the work-order query, and the resulting refetch
    // would otherwise clobber changes the user is still making (#557).
    // A blank row is not sent (#4125). Keep it across the refetch that follows a save;
    // marking the form clean must not let this effect throw the row away.
    if (isDirtyRef.current || savingRef.current || unsentBlankRowsRef.current) return;
    const incoming = incomingBaseRef.current;
    if (incoming && baseRef.current && Date.parse(incoming.updatedAt) < Date.parse(baseRef.current.updatedAt)) return;
    baseRef.current = incoming;
    setSelectedMechanicId(mechanicId ?? '');
    if (bookingServiceData) {
      setTitle(distinctServiceTitle(bookingServiceData.name, bookingServiceData.title) ?? '');
      setNotes(bookingServiceData.notes);
      setLaborItems((prev) => mergeLaborItemDrafts(prev, bookingServiceData.laborItems));
      const mappedParts = bookingServiceData.parts.map((p) => ({
        id: p.id,
        partCategoryId: p.partCategoryId,
        partId: p.partId ?? null,
        name: p.name,
        isRequired: p.isRequired,
        quantity: p.quantity,
        unitPriceGrossCents: p.unitPriceGrossCents,
        vatPercent: p.vatPercent,
        discountPercent: p.discountPercent ?? 0,
        notes: p.notes,
        sortOrder: p.sortOrder,
      }));
      setParts(mappedParts);
      writePricing((current) => remapRowPricing(current, undefined, mappedParts));
      setPriceInputs(
        mappedParts.map((p) => (p.unitPriceGrossCents / 100).toFixed(2))
      );
      setQuantityInputs(
        mappedParts.map((p) => String(p.quantity))
      );
      const mappedExtras = bookingServiceData.extras.map((e) => ({
        id: e.id,
        description: e.description,
        amountGrossCents: e.amountGrossCents,
        isTaxable: e.isTaxable,
        hasVariableQuantity: e.hasVariableQuantity,
        unit: e.unit,
        quantity: e.quantity,
        sortOrder: e.sortOrder,
      }));
      setExtras(mappedExtras);
      setExtraPriceInputs(
        mappedExtras.map((e) => (e.amountGrossCents / 100).toFixed(2))
      );
      setExtraQuantityInputs(
        mappedExtras.map((e) => e.quantity != null ? String(e.quantity) : '')
      );
    } else {
      setTitle('');
      setNotes(null);
      setLaborItems([]);
      setParts([]);
      writePricing(() => ({}));
      setPriceInputs([]);
      setQuantityInputs([]);
      setExtras([]);
      setExtraPriceInputs([]);
      setExtraQuantityInputs([]);
    }
    setIsDirty(false);
  }, [mechanicId, bookingServiceData, incomingToken, reloadGeneration, writePricing]);

  const commitMechanic = async (
    value: string,
    options?: { skillGateOverride: SkillGateOverride },
  ) => {
    if (options) {
      await onMechanicChange(bookingServiceId, value || null, options);
    } else {
      await onMechanicChange(bookingServiceId, value || null);
    }
  };

  const handleMechanicChange = (value: string) => {
    const previous = selectedMechanicId;
    setSelectedMechanicId(value);
    if (value !== '') mechanicMru.record(value);
    void (async () => {
      try {
        await commitMechanic(value);
      } catch (error) {
        const parsed = parseCertificateGateError(error);
        if (parsed) {
          setPendingMechanic({ value, previous });
          setCertificateGate(parsed);
          return;
        }
        if (isCertificateOverrideForbiddenError(error)) {
          notify.error(tc('error'), t('certificateGate.forbidden'));
        }
        setSelectedMechanicId(previous);
      }
    })();
  };

  const handleCertificateOverrideClose = () => {
    if (overrideSubmitting) return;
    if (pendingMechanic) setSelectedMechanicId(pendingMechanic.previous);
    setCertificateGate(null);
    setPendingMechanic(null);
  };

  const handleCertificateOverrideSubmit = async (reason: string) => {
    if (!pendingMechanic) return;
    setOverrideSubmitting(true);
    try {
      await commitMechanic(pendingMechanic.value, { skillGateOverride: { reason } });
      setCertificateGate(null);
      setPendingMechanic(null);
    } finally {
      setOverrideSubmitting(false);
    }
  };

  const latestRef = useRef({ laborItems, parts, extras, notes, title, priceInputs, quantityInputs, extraPriceInputs, extraQuantityInputs });
  latestRef.current = { laborItems, parts, extras, notes, title, priceInputs, quantityInputs, extraPriceInputs, extraQuantityInputs };
  const hasUncommittedInputs = useCallback((draft: typeof latestRef.current) =>
    draft.parts.some((p, i) => draft.priceInputs[i] !== (p.unitPriceGrossCents / 100).toFixed(2) || draft.quantityInputs[i] !== String(p.quantity)) ||
    draft.extras.some((e, i) => draft.extraPriceInputs[i] !== (e.amountGrossCents / 100).toFixed(2) || draft.extraQuantityInputs[i] !== (e.quantity == null ? '' : String(e.quantity))), []);
  const autosave = useCallback(async () => {
    // A row whose price choice is unresolved is not saved: the save waits for the pick to settle (it is retried then).
    if (savingRef.current || conflictBlockedRef.current || !baseRef.current || hasUncommittedInputs(latestRef.current) || hasPendingPick(pricingRef.current)) return;
    const sent = toDetailsPayload(laborItems, parts, extras, notes, title, laborLocked);
    // Nothing new to send: a blank row is not a change. Stay clean, but remember the row so a refetch cannot drop it.
    if (JSON.stringify(sent) === lastSavedPayloadRef.current) {
      unsentBlankRowsRef.current = parts.some(isBlankPartDraft) || extras.some(isBlankExtraDraft);
      if (!unmountedRef.current) setIsDirty(false);
      return;
    }
    savingRef.current = true;
    setLocalSaveError(false);
    setSaveErrorText(null);
    const base = baseRef.current;
    try {
      const saved = await updateDetails.mutateAsync({ workOrderId, bookingServiceId, data: sent, base });
      const current = latestRef.current;
      const desired = toDetailsPayload(current.laborItems, current.parts, current.extras, current.notes, current.title, laborLocked);
      const editedDuringSave = JSON.stringify(desired) !== JSON.stringify(sent);
      // The committed response supplies exact identities; newer local edits remain dirty.
      baseRef.current = saved;
      const merged = editedDuringSave ? reconcileAfterServiceDetailsSave(sent, desired, saved) : serviceDetailsPayload(saved.service);
      // An unmount during the first PUT still owes the newer committed edits a save.
      // Use this transaction's token/identities and keep the same conditional recovery.
      if (unmountedRef.current) {
        if (editedDuringSave && !hasUncommittedInputs(current))
          await updateDetails.mutateAsync({ workOrderId, bookingServiceId, data: merged, base: saved });
        onSaved?.();
        return;
      }
      const nextTitle = merged.title ?? '';
      const nextNotes = merged.notes ?? null;
      const nextLabor = mergeLaborItemDrafts(current.laborItems, (merged.laborItems ?? []).map(row => ({ ...current.laborItems.find(x => x.id === row.id || saved.idMap?.[x.id] === row.id), ...saved.service.laborItems.find(x => x.id === row.id), ...row })) as BookingServiceLaborItem[]);
      // Blank rows were not in the request, so the response does not have them. Put them back (#4125).
      const nextParts = restoreOmittedBlankRows(current.parts, toPartDrafts(merged.parts), isBlankPartDraft);
      const nextExtras = restoreOmittedBlankRows(current.extras, merged.extras ?? [], isBlankExtraDraft);
      setTitle(nextTitle);
      setNotes(nextNotes);
      setLaborItems(nextLabor);
      setParts(nextParts);
      writePricing((current) => remapRowPricing(current, saved.idMap, nextParts));
      setExtras(nextExtras);
      const retainInputs = <T extends { id?: string }>(rows: T[], oldRows: { id?: string }[], localRows: { id?: string }[], localText: string[], oldText: string[], format: (row: T) => string) => rows.map(row => {
        const same = (old: { id?: string }) => old.id === row.id || (old.id && saved.idMap?.[old.id] === row.id);
        const localIndex = localRows.findIndex(same);
        const oldIndex = oldRows.findIndex(same);
        return localIndex >= 0 && (oldIndex < 0 || localText[localIndex] !== oldText[oldIndex])
          ? localText[localIndex] ?? format(row) : format(row);
      });
      setPriceInputs(retainInputs(nextParts, sent.parts, current.parts, current.priceInputs, priceInputs, p => (p.unitPriceGrossCents / 100).toFixed(2)));
      setQuantityInputs(retainInputs(nextParts, sent.parts, current.parts, current.quantityInputs, quantityInputs, p => String(p.quantity)));
      setExtraPriceInputs(retainInputs(nextExtras, sent.extras, current.extras, current.extraPriceInputs, extraPriceInputs, e => (e.amountGrossCents / 100).toFixed(2)));
      setExtraQuantityInputs(retainInputs(nextExtras, sent.extras, current.extras, current.extraQuantityInputs, extraQuantityInputs, e => e.quantity == null ? '' : String(e.quantity)));
      unsentBlankRowsRef.current = nextParts.some(isBlankPartDraft) || nextExtras.some(isBlankExtraDraft);
      // Only what this PUT committed. In-flight edits stay dirty so the next autosave sends them.
      const settled = toDetailsPayload(nextLabor, nextParts, nextExtras, nextNotes, nextTitle, laborLocked);
      lastSavedPayloadRef.current = JSON.stringify(editedDuringSave ? sent : settled);
      setSaveErrorText(null);
      // A leftover blank row is not an unsaved edit: it was intentionally left unsent, and the ref above keeps it.
      setIsDirty(editedDuringSave || hasUncommittedInputs(current));
      onSaved?.();
    } catch (error) {
      if (error instanceof ServiceDetailsConflict) { conflictBlockedRef.current = true; if (!unmountedRef.current) setConflictBlocked(true); }
      if (!unmountedRef.current) setLocalSaveError(true);
      // Conflicts keep the generic toast. Any other failure says why (#4125, #3352).
      const message = error instanceof ServiceDetailsConflict
        ? t('detail.detailsSaveFailed')
        : describeApiError(error, tc, { fallback: t('detail.detailsSaveFailed') });
      if (!(error instanceof ServiceDetailsConflict) && !unmountedRef.current) setSaveErrorText(message);
      notify.error(tc('error'), message);
    } finally { savingRef.current = false; }
  }, [updateDetails, workOrderId, bookingServiceId, laborItems, parts, extras, notes, title, laborLocked, priceInputs, quantityInputs, extraPriceInputs, extraQuantityInputs, hasUncommittedInputs, onSaved, notify, t, tc, writePricing]);

  // Autosave (#557): persist committed changes a short moment after the last
  // edit. Free-text money/quantity fields only reach committed state (parts /
  // extras) on blur, so this debounced snapshot never holds a half-typed value.
  // Notes IS free text but is short/rare enough to autosave on every keystroke
  // (debounced) rather than adding a blur-commit step just for this field.
  const committedSnapshot = useMemo(
    () => ({ laborItems, parts, extras, notes, title }),
    [laborItems, parts, extras, notes, title],
  );
  const debouncedSnapshot = useDebounce(committedSnapshot, 800);
  const autosaveRef = useRef(autosave);
  autosaveRef.current = autosave;
  useEffect(() => {
    if (isDirtyRef.current) void autosaveRef.current();
  }, [debouncedSnapshot]);
  // The save that waited for an unresolved price choice runs as soon as the choice has settled (applied, failed or abandoned).
  useEffect(() => {
    if (!pricingBusy && isDirtyRef.current) void autosaveRef.current();
  }, [pricingBusy]);

  // Flush a pending edit if the user leaves within the debounce window so the
  // last change isn't lost. Fire-and-forget on unmount (no setState).
  const flushRef = useRef<() => void>(() => {});
  flushRef.current = () => {
    const draft = latestRef.current;
    if (!baseRef.current || savingRef.current || conflictBlockedRef.current || hasUncommittedInputs(draft)) return;
    const data = toDetailsPayload(draft.laborItems, draft.parts, draft.extras, draft.notes, draft.title, laborLocked);
    if (JSON.stringify(data) === lastSavedPayloadRef.current) return;
    updateDetails.mutate({ workOrderId, bookingServiceId, data, base: baseRef.current });
  };
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      if (isDirtyRef.current) flushRef.current();
    };
  }, []);

  // Labor items handlers
  const addLaborItem = () => {
    setLaborItems((prev) => [
      ...prev,
      {
        id: `temp-${crypto.randomUUID()}`,
        clientKey: crypto.randomUUID(),
        description: '',
        estimatedHours: 0.5,
        discountPercent: 0,
        sortOrder: prev.length,
        status: 'pending' as const,
        startedAt: null,
        completedAt: null,
        completedBy: null,
        timeSource: 'manual',
        estimatedHoursOriginal: 0.5,
        editedBy: null,
        editedAt: null,
        editedByName: null,
      },
    ]);
    setIsDirty(true);
  };

  const updateLaborItem = <K extends keyof BookingServiceLaborItem>(
    index: number,
    field: K,
    value: BookingServiceLaborItem[K],
  ) => {
    setLaborItems((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
    setIsDirty(true);
  };

  const restoreLaborItemHours = (index: number) => {
    setLaborItems((prev) =>
      prev.map((item, i) => {
        if (i !== index || item.estimatedHoursOriginal == null) return item;
        return {
          ...item,
          estimatedHours: item.estimatedHoursOriginal,
          editedBy: null,
          editedAt: null,
          editedByName: null,
        };
      }),
    );
    setIsDirty(true);
  };

  const removeLaborItem = (index: number) => {
    setLaborItems((prev) => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const applyHaynesProAdoption = (result: HaynesProAdoptResult) => {
    // Patch only the adopted row. Other unsaved labor drafts stay as-is even
    // if the work-order query invalidates — the dirty guard still skips
    // full rehydration (#1442).
    setLaborItems((prev) => applyAdoptedLaborItem(prev, result.laborItem));
  };

  const bookingDurationHours = sumHours(laborItems.map((item) => item.estimatedHours)) + otherLaborHours;
  const hasScheduleConflict = durationExceedsAllocatedSlot(bookingDurationHours, allocatedSlotHours);

  // Parts handlers
  const addPart = () => {
    setParts((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        partCategoryId: null,
        partId: null,
        name: '',
        isRequired: true,
        quantity: 1,
        unitPriceGrossCents: 0,
        vatPercent: defaultVatPercent,
        discountPercent: 0,
        notes: null,
        sortOrder: prev.length,
      },
    ]);
    setPriceInputs((prev) => [...prev, '0.00']);
    setQuantityInputs((prev) => [...prev, '1']);
    setIsDirty(true);
  };

  // Part bundle (#3350, #3034): append every expanded line as an ordinary part row at its server-resolved price. The picker
  // resolved the prices of the whole bundle before calling (all or nothing), so there is no partial insert and the rows are
  // built completely before any state changes. The resolved NET price becomes whole gross cents at the rate the row carries.
  // Functional updates keep rows and typed values added while the bundle was loading, and the three parallel arrays grow
  // together exactly as in addPart. Ids are temporary (`temp-…`), so the shared writer strips them and the server assigns the
  // real ones; the normal autosave (version token, conflict handling, merge-after-save) is the only write path.
  const addBundleParts = (expansion: PartBundleExpansion, prices: ReadonlyMap<string, ResolvedPartLinePrice>) => {
    if (expansion.lines.length === 0) return;
    const added = expansion.lines.map((line) => {
      const price = prices.get(line.partId);
      if (!price) throw new Error(`No resolved price for part ${line.partId}`);
      return {
        row: {
          id: `temp-${crypto.randomUUID()}`,
          partCategoryId: line.categoryId,
          // The real catalog part (#3842); the price is the server-resolved one below, never the expansion's catalog price.
          partId: line.partId,
          name: line.name,
          isRequired: true,
          quantity: line.quantity,
          unitPriceGrossCents: resolvedUnitGrossCents(price.unitNetCents, expansion.vatPercent),
          vatPercent: expansion.vatPercent,
          discountPercent: 0,
          notes: null,
        },
        price,
      };
    });
    setParts((prev) => [...prev, ...added.map(({ row }, offset) => ({ ...row, sortOrder: prev.length + offset }))]);
    setPriceInputs((prev) => [...prev, ...added.map(({ row }) => (row.unitPriceGrossCents / 100).toFixed(2))]);
    setQuantityInputs((prev) => [...prev, ...added.map(({ row }) => String(row.quantity))]);
    writePricing((current) => ({
      ...current,
      ...Object.fromEntries(
        added.map(({ row, price }) => [
          row.id,
          { kind: 'resolved', partId: price.partId, source: price.source, grossCents: row.unitPriceGrossCents } satisfies PartRowPricing,
        ]),
      ),
    }));
    setIsDirty(true);
  };

  // Catalog pick (#3034): the price is the server's resolution for THIS work order, never the catalog price of the part list.
  // The row is left alone until the answer is in and still wanted: a later pick, a typed price or name, a removed row, another
  // work order, an unmount, or a changed session or permission makes the answer void, silently. A failure is shown on the row
  // and applies nothing.
  const startPick = (rowId: string, part: PickedPart) => {
    // The owner of this pick is the live owner NOW, taken from the render that offered it; null when it may not price (refused
    // below, nothing is sent).
    let fence: PricingFence | null = null;
    try {
      fence = takeFence(pricingAccess, PART_RESOLVE_PERMISSIONS);
    } catch {
      fence = null;
    }
    const entry: PartRowPricing = { kind: 'pending', part, workOrderId, fence };
    writePricing((current) => ({ ...current, [rowId]: entry }));
    const live = () =>
      !unmountedRef.current &&
      workOrderIdRef.current === workOrderId &&
      Object.values(pricingRef.current).includes(entry) &&
      (fence === null || fenceHolds(fence));
    const dropEntry = () => {
      writePricing((current) => {
        const key = Object.keys(current).find((candidate) => current[candidate] === entry);
        if (key === undefined) return current;
        const { [key]: _gone, ...rest } = current;
        return rest;
      });
    };
    const fail = (reason: PartPriceFailureReason) => {
      if (!live()) return;
      writePricing((current) => {
        const key = Object.keys(current).find((candidate) => current[candidate] === entry);
        return key === undefined ? current : { ...current, [key]: { kind: 'failed', part, reason, fence } };
      });
      notify.error(tc('error'), `${t('detail.partPricing.notApplied')} ${t(partPriceFailureLabelKey(reason))}`);
    };
    if (fence === null || !pricing.canResolve) {
      fail('forbidden');
      return;
    }
    pricing.resolve([part.id]).then(
      (prices) => {
        if (!live()) return;
        const price = prices.get(part.id);
        if (!price) {
          fail('invalid_response');
          return;
        }
        setResolvedPicks((queue) => [...queue, { entry, price }]);
      },
      (error: unknown) => {
        if (isSessionCancel(error)) {
          // The session or a permission changed: nothing is shown or applied; the row is simply not waiting any more.
          if (live()) dropEntry();
          return;
        }
        fail(asPartPriceFailure(error)?.reason ?? 'request_failed');
      },
    );
  };

  // Applies the answers of live picks from an effect, so the row index and the row's current VAT rate are those of the
  // committed draft and not of a render that a pending update has already passed. A name, category, part identity and price land together.
  useEffect(() => {
    if (resolvedPicks.length === 0) return;
    setResolvedPicks([]);
    for (const { entry, price } of resolvedPicks) {
      const rowId = Object.keys(pricingRef.current).find((key) => pricingRef.current[key] === entry);
      if (rowId === undefined || entry.kind !== 'pending' || workOrderIdRef.current !== entry.workOrderId) continue;
      // The answer was resolved under the owner that started the pick; it is applied only if that owner is still the live one
      // when the effect commits (the session may have changed in between). Otherwise the row just stops waiting.
      if (entry.fence === null || !fenceHolds(entry.fence)) {
        writePricing((current) => {
          const { [rowId]: _gone, ...rest } = current;
          return rest;
        });
        continue;
      }
      const index = parts.findIndex((row) => row.id === rowId);
      const row = parts[index];
      if (!row) {
        writePricing((current) => remapRowPricing(current, undefined, parts));
        continue;
      }
      const grossCents = resolvedUnitGrossCents(price.unitNetCents, row.vatPercent);
      setParts((prev) =>
        prev.map((candidate) =>
          candidate.id === rowId
            ? {
                ...candidate,
                name: entry.part.name,
                // A slot from a required category keeps its category; the item fills it (#3842).
                partCategoryId: candidate.partCategoryId ?? entry.part.categoryId,
                // The picked part's identity lands with its resolved price, only here (an owned, still-wanted answer).
                partId: price.partId,
                unitPriceGrossCents: grossCents,
              }
            : candidate,
        ),
      );
      setPriceInputs((prev) => prev.map((value, i) => (i === index ? (grossCents / 100).toFixed(2) : value)));
      writePricing((current) => ({ ...current, [rowId]: { kind: 'resolved', partId: price.partId, source: price.source, grossCents } }));
      setIsDirty(true);
    }
  }, [resolvedPicks, parts, writePricing]);

  // Another work order is another price context: nothing resolved or pending for the previous one carries over.
  useEffect(() => {
    writePricing(() => ({}));
    setResolvedPicks([]);
  }, [workOrderId, writePricing]);

  const clearRowPricing = (rowId: string | undefined) => {
    if (rowId === undefined || !(rowId in pricingRef.current)) return;
    writePricing((current) => {
      const { [rowId]: _gone, ...rest } = current;
      return rest;
    });
  };

  const updatePart = (index: number, field: keyof PartDraft, value: string | number | boolean | null) => {
    setParts((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
    setIsDirty(true);
  };

  const handleQuantityInputChange = (index: number, value: string) => {
    setQuantityInputs((prev) => prev.map((v, i) => (i === index ? value : v)));
    setIsDirty(true);
  };

  const handleQuantityBlur = (index: number) => {
    const raw = quantityInputs[index] ?? '0';
    const normalized = raw.replace(',', '.');
    const qty = parseFloat(normalized);
    const validQty = isNaN(qty) || qty < 0 ? 0 : qty;
    updatePart(index, 'quantity', validQty);
    setQuantityInputs((prev) =>
      prev.map((v, i) => (i === index ? String(validQty) : v))
    );
  };

  const handlePriceInputChange = (index: number, value: string) => {
    // Typing a price abandons a pick that is still waiting for its (or failed to get a) resolved price: the user chose.
    const entry = pricingRef.current[parts[index]?.id ?? ''];
    if (entry && (entry.kind === 'pending' || entry.kind === 'failed')) clearRowPricing(parts[index]?.id);
    setPriceInputs((prev) => prev.map((v, i) => (i === index ? value : v)));
    setIsDirty(true);
  };

  const handlePriceBlur = (index: number) => {
    const raw = (priceInputs[index] ?? '0').replace(',', '.');
    const grossCents = eurosToCents(raw) ?? 0;
    // A resolved price changed by hand is a manual price from now on; the same amount typed again is not.
    const rowId = parts[index]?.id;
    const entry = rowId === undefined ? undefined : pricingRef.current[rowId];
    if (rowId !== undefined && entry?.kind === 'resolved' && grossCents !== entry.grossCents) {
      writePricing((current) => ({ ...current, [rowId]: { kind: 'manual', partId: entry.partId } }));
    }
    updatePart(index, 'unitPriceGrossCents', grossCents);
    // Normalize the displayed text (e.g. "1.5" -> "1.50") from the same gross
    // cents just stored — never re-derived via net, so it can't drift (#1022).
    setPriceInputs((prev) =>
      prev.map((v, i) => (i === index ? (grossCents / 100).toFixed(2) : v))
    );
  };

  const handlePartRowKeyDown = (index: number, event: KeyboardEvent) => {
    if (!shouldAddProductRowOnEnter(event)) return;
    event.preventDefault();
    handleQuantityBlur(index);
    handlePriceBlur(index);
    focusNewPartNameRef.current = true;
    addPart();
  };

  // The hours field commits its draft on blur, which the focus move to the new
  // row triggers, so nothing typed into hours is lost (#3276).
  const handleLaborRowKeyDown = (event: KeyboardEvent) => {
    if (!shouldAddProductRowOnEnter(event)) return;
    event.preventDefault();
    focusNewLaborDescriptionRef.current = true;
    addLaborItem();
  };

  const handleVatChange = (index: number, newVatPercent: number) => {
    // The gross price the user sees/typed is unaffected by the VAT rate —
    // only the (server-derived) net split changes, so just update vatPercent.
    setParts((prev) =>
      prev.map((item, i) => (i === index ? { ...item, vatPercent: newVatPercent } : item))
    );
    setIsDirty(true);
  };

  const removePart = (index: number) => {
    clearRowPricing(parts[index]?.id);
    setParts((prev) => prev.filter((_, i) => i !== index));
    setPriceInputs((prev) => prev.filter((_, i) => i !== index));
    setQuantityInputs((prev) => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  // Extras handlers
  const addExtra = () => {
    setExtras((prev) => [
      ...prev,
      { id: crypto.randomUUID(), description: '', amountGrossCents: 0, isTaxable: true, hasVariableQuantity: false, unit: null, quantity: null, sortOrder: prev.length },
    ]);
    setExtraPriceInputs((prev) => [...prev, '0.00']);
    setExtraQuantityInputs((prev) => [...prev, '']);
    setIsDirty(true);
  };

  const updateExtra = (index: number, field: keyof ExtraDraft, value: string | number | boolean | null) => {
    setExtras((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
    setIsDirty(true);
  };

  const handleExtraPriceInputChange = (index: number, value: string) => {
    setExtraPriceInputs((prev) => prev.map((v, i) => (i === index ? value : v)));
    setIsDirty(true);
  };

  const handleExtraPriceBlur = (index: number) => {
    const raw = (extraPriceInputs[index] ?? '0').replace(',', '.');
    const cents = eurosToCents(raw) ?? 0;
    updateExtra(index, 'amountGrossCents', cents);
    setExtraPriceInputs((prev) =>
      prev.map((v, i) => (i === index ? (cents / 100).toFixed(2) : v))
    );
  };

  const handleExtraQuantityInputChange = (index: number, value: string) => {
    setExtraQuantityInputs((prev) => prev.map((v, i) => (i === index ? value : v)));
    setIsDirty(true);
  };

  const handleExtraQuantityBlur = (index: number) => {
    const raw = (extraQuantityInputs[index] ?? '0').replace(',', '.');
    const qty = parseFloat(raw);
    const validQty = isNaN(qty) || qty < 0 ? null : qty;
    updateExtra(index, 'quantity', validQty);
    setExtraQuantityInputs((prev) =>
      prev.map((v, i) => (i === index ? (validQty != null ? String(validQty) : '') : v))
    );
  };

  const removeExtra = (index: number) => {
    setExtras((prev) => prev.filter((_, i) => i !== index));
    setExtraPriceInputs((prev) => prev.filter((_, i) => i !== index));
    setExtraQuantityInputs((prev) => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  // Hand-set status stays off PartDraft and the details PUT (#3568). A new row's
  // client UUID matches the persisted-id shape but is absent here until the server has it.
  const partOrderStatusById = useMemo(() => {
    const byId = new Map<string, { orderStatus: PartOrderStatus | null; orderStatusSetAt: string | null }>();
    for (const part of bookingServiceData?.parts ?? []) {
      byId.set(part.id, {
        orderStatus: part.orderStatus ?? null,
        orderStatusSetAt: part.orderStatusSetAt ?? null,
      });
    }
    return byId;
  }, [bookingServiceData?.parts]);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Assigned mechanic */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('detail.assignedMechanic')}
          </Typography>
          {mechanicName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <UserAvatar
                firstName={mechanicName.split(' ')[0] ?? ''}
                lastName={mechanicName.split(' ').slice(1).join(' ') ?? ''}
                size={28}
              />
              <Typography variant="body2">{mechanicName}</Typography>
            </Box>
          )}
          <FormControl fullWidth size="small">
            <InputLabel id="service-detail-mechanic-label">{t('detail.changeMechanic')}</InputLabel>
            <Select
              labelId="service-detail-mechanic-label"
              id="service-detail-mechanic"
              value={selectedMechanicId}
              onChange={(e) => handleMechanicChange(e.target.value)}
              label={t('detail.changeMechanic')}
              disabled={saving}
            >
              <MenuItem value="">
                <em>{t('detail.noMechanic')}</em>
              </MenuItem>
              {mechanicMru.order(eligibleMechanics, (m) => m.id).map((m) => (
                <MenuItem
                  key={m.id}
                  value={m.id}
                  sx={hasSkillRequirements ? { color: 'success.main', fontWeight: 500 } : undefined}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box component="span" sx={{ flex: 1 }}>
                      {m.firstName} {m.lastName}
                    </Box>
                    <MechanicCertificateLock
                      blocked={mechanicCertificateIssue(m, requiredCertificateSkills) !== null}
                    />
                  </Box>
                </MenuItem>
              ))}
              {hasSkillRequirements && ineligibleMechanics.length > 0 && [
                <MenuItem key="__divider" disabled divider />,
                ...mechanicMru.order(ineligibleMechanics, (m) => m.id).map((m) => (
                  <MenuItem key={m.id} value={m.id} sx={{ color: 'text.secondary' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box component="span" sx={{ flex: 1 }}>
                        {m.firstName} {m.lastName}
                      </Box>
                      <MechanicCertificateLock
                        blocked={mechanicCertificateIssue(m, requiredCertificateSkills) !== null}
                      />
                    </Box>
                  </MenuItem>
                )),
              ]}
            </Select>
          </FormControl>
          {selectedIsIneligible && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
              {t('detail.mechanicMissingSkills')}
            </Typography>
          )}
        </Box>

        <Divider />

        <TextField
          label={t('serviceTitle')}
          helperText={t('serviceTitleHint')}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            setIsDirty(true);
          }}
          inputProps={{ maxLength: 200 }}
          size="small"
          fullWidth
        />

        {/* Task notes — customer-facing, shown on the invoice/work-order print (#1754) */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('detail.taskNotes')}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            placeholder={t('detail.taskNotesPlaceholder')}
            helperText={t('detail.taskNotesHint')}
            value={notes ?? ''}
            onChange={(e) => {
              setNotes(e.target.value === '' ? null : e.target.value);
              setIsDirty(true);
            }}
          />
        </Box>

        <Divider />

        {/* Labor items */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('detail.serviceLaborSteps')}
            </Typography>
            {laborEditable && (
              <Tooltip title={t('detail.addLaborItem')}>
                <IconButton size="small" onClick={addLaborItem} color="primary" aria-label={t('detail.addLaborItem')}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {laborLocked && (
            <Alert severity="info" role="status" sx={{ mb: 1 }}>
              {t('detail.fixedLaborLocked')}
            </Alert>
          )}
          {laborEditable && haynesProEnabled && haynesProBlocked && (
            <Alert severity="info" role="status" sx={{ mb: 1 }}>
              {t('detail.haynesProTime.saveFirst')}
            </Alert>
          )}
          {laborItems.map((item, index) => (
            <LaborItemRow
              key={item.clientKey}
              item={item}
              index={index}
              isEditable={!laborLocked}
              hoursEditable={laborEditable}
              workOrderId={workOrderId}
              serviceId={bookingServiceId}
              serviceStatus={serviceStatus}
              statusActionsLocked={statusActionsLocked}
              onUpdate={updateLaborItem}
              onRestoreHours={laborEditable ? restoreLaborItemHours : undefined}
              onRemove={laborEditable ? removeLaborItem : undefined}
              onRowKeyDown={laborEditable ? handleLaborRowKeyDown : undefined}
              descriptionInputRef={(el) => {
                laborDescriptionInputRefs.current[index] = el;
              }}
              laborRateCents={pricingType === 'fixed' ? null : laborRateCents}
              formatPrice={formatPrice}
              haynesProEnabled={haynesProEnabled}
              haynesProBlocked={haynesProBlocked}
              vehicleId={vehicleId}
              vehicleMake={vehicleMake}
              vehicleModel={vehicleModel}
              pricingType={pricingType}
              onHaynesProAdopted={applyHaynesProAdoption}
            />
          ))}
          {laborItems.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              —
            </Typography>
          )}
          {laborItems.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t('detail.bookingDurationPreview', { duration: formatDuration(bookingDurationHours) })}
            </Typography>
          )}
          {hasScheduleConflict && allocatedSlotHours != null && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t('detail.scheduleConflictWarning', {
                duration: formatDuration(bookingDurationHours),
                slot: formatDuration(allocatedSlotHours),
              })}
            </Alert>
          )}
        </Box>

        <Divider />

        {/* Parts */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('detail.serviceParts')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PartBundlePicker workOrderId={workOrderId} onInsert={addBundleParts} />
              <Tooltip title={t('detail.addPart')}>
                <IconButton size="small" onClick={addPart} color="primary" aria-label={t('detail.addPart')}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          {parts.map((item, index) => (
            <Box
              key={index}
              sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}
              onKeyDown={(event) => handlePartRowKeyDown(index, event)}
            >
              <PartCatalogInput
                value={item.name}
                onChange={(name) => {
                  // The row no longer is the picked part: its price source says nothing about the typed text, and a hand-typed
                  // name does not keep the catalog identity (#3842). The category slot of a required category stays.
                  clearRowPricing(item.id);
                  setParts((prev) => prev.map((row, i) => (i === index ? { ...row, name, partId: null } : row)));
                  setIsDirty(true);
                }}
                onSelect={(part) => {
                  if (item.id === undefined) return;
                  startPick(item.id, { id: part.id, name: part.name, categoryId: part.categoryId });
                }}
                inputRef={(el) => { partNameInputRefs.current[index] = el; }}
              />
              <TextField
                size="small"
                sx={{ width: 80, flexShrink: 0 }}
                placeholder={t('detail.partQuantity')}
                value={quantityInputs[index] ?? ''}
                onChange={(e) => handleQuantityInputChange(index, e.target.value)}
                onBlur={() => handleQuantityBlur(index)}
                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              />
              <TextField
                size="small"
                sx={{ width: 110, flexShrink: 0 }}
                placeholder={t('detail.partPrice')}
                value={priceInputs[index] ?? '0.00'}
                onChange={(e) => handlePriceInputChange(index, e.target.value)}
                onBlur={() => handlePriceBlur(index)}
                slotProps={{
                  htmlInput: { inputMode: 'decimal' },
                  input: {
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  },
                }}
              />
              <VatRatePicker
                value={item.vatPercent}
                rates={vatRates}
                onChange={(percent) => handleVatChange(index, percent)}
                label={t('detail.partVat')}
              />
              <TextField
                size="small"
                type="number"
                sx={{ width: 90, flexShrink: 0 }}
                placeholder={t('detail.partDiscount')}
                value={item.discountPercent}
                onChange={(e) => {
                  const val = parseFloat(e.target.value) || 0;
                  updatePart(index, 'discountPercent', Math.max(0, Math.min(100, val)));
                }}
                slotProps={{
                  htmlInput: { min: 0, max: 100, step: '0.1' },
                  input: {
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  },
                }}
              />
              <PartOrderStatusSelect
                workOrderId={workOrderId}
                bookingServiceId={bookingServiceId}
                partId={item.id !== undefined && partOrderStatusById.has(item.id) ? item.id : undefined}
                value={(item.id ? partOrderStatusById.get(item.id)?.orderStatus : null) ?? null}
                orderStatusSetAt={item.id ? partOrderStatusById.get(item.id)?.orderStatusSetAt ?? null : null}
              />
              <Typography variant="body2" sx={{ flexShrink: 0, minWidth: 80, textAlign: 'right', fontWeight: 500 }}>
                {/* Discount-aware (#1815) — used to ignore item.discountPercent, so the
                    figure shown here didn't match what the customer is actually billed. */}
                = {(lineNetAfterDiscountCents(item.unitPriceGrossCents, item.quantity, item.discountPercent) / 100).toFixed(2)} €
              </Typography>
              <Tooltip title={t('detail.removePart')}>
                <IconButton
                  size="small"
                  onClick={() => removePart(index)}
                  color="error"
                  tabIndex={-1}
                  aria-label={t('detail.removePart')}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <PartPriceNote
                entry={item.id === undefined ? undefined : rowPricing[item.id]}
                grossCents={item.unitPriceGrossCents}
                onRetry={(part) => {
                  if (item.id === undefined) return;
                  // A retry belongs to the owner whose lookup failed; under any other owner it is dropped, not repeated.
                  const failed = pricingRef.current[item.id];
                  if (failed?.kind === 'failed' && failed.fence !== null && !fenceHolds(failed.fence)) {
                    clearRowPricing(item.id);
                    return;
                  }
                  startPick(item.id, part);
                }}
                onDismiss={() => clearRowPricing(item.id)}
              />
            </Box>
          ))}
          {parts.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              —
            </Typography>
          )}
        </Box>

        <Divider />

        {/* Extras */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('detail.serviceExtras')}
            </Typography>
            <Tooltip title={t('detail.addExtra')}>
              <IconButton size="small" onClick={addExtra} color="primary" aria-label={t('detail.addExtra')}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {extras.map((item, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                size="small"
                sx={{ flex: 1, minWidth: 120 }}
                placeholder={t('detail.extraDescription')}
                value={item.description}
                onChange={(e) => updateExtra(index, 'description', e.target.value)}
              />
              {item.hasVariableQuantity ? (
                <>
                  <TextField
                    size="small"
                    sx={{ width: 110, flexShrink: 0 }}
                    placeholder={t('detail.extraUnitPrice')}
                    value={extraPriceInputs[index] ?? '0.00'}
                    onChange={(e) => handleExtraPriceInputChange(index, e.target.value)}
                    onBlur={() => handleExtraPriceBlur(index)}
                    slotProps={{
                      htmlInput: { inputMode: 'decimal' },
                      input: {
                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                        endAdornment: item.unit ? (
                          <InputAdornment position="end">/{item.unit}</InputAdornment>
                        ) : undefined,
                      },
                    }}
                  />
                  <TextField
                    size="small"
                    sx={{ width: 100, flexShrink: 0 }}
                    placeholder={t('detail.extraQuantity')}
                    value={extraQuantityInputs[index] ?? ''}
                    onChange={(e) => handleExtraQuantityInputChange(index, e.target.value)}
                    onBlur={() => handleExtraQuantityBlur(index)}
                    slotProps={{
                      htmlInput: { inputMode: 'decimal' },
                    }}
                  />
                  <UnitSelect
                    value={item.unit}
                    onChange={(unit) => updateExtra(index, 'unit', unit)}
                    label={t('detail.extraUnit')}
                  />
                  <Typography variant="body2" sx={{ flexShrink: 0, minWidth: 70, textAlign: 'right' }}>
                    = {(lineTotalGrossCents(item.amountGrossCents, item.quantity ?? 0) / 100).toFixed(2)} €
                  </Typography>
                </>
              ) : (
                <TextField
                  size="small"
                  sx={{ width: 120, flexShrink: 0 }}
                  placeholder={t('detail.extraAmount')}
                  value={extraPriceInputs[index] ?? '0.00'}
                  onChange={(e) => handleExtraPriceInputChange(index, e.target.value)}
                  onBlur={() => handleExtraPriceBlur(index)}
                  slotProps={{
                    htmlInput: { inputMode: 'decimal' },
                    input: {
                      startAdornment: <InputAdornment position="start">€</InputAdornment>,
                    },
                  }}
                />
              )}
              <Tooltip title={t('detail.removeExtra')}>
                <IconButton
                  size="small"
                  onClick={() => removeExtra(index)}
                  color="error"
                  tabIndex={-1}
                  aria-label={t('detail.removeExtra')}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
          {extras.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              —
            </Typography>
          )}
        </Box>

        {/* Action row: remove service + autosave status (#557) */}
        <Box sx={{ display: 'flex', gap: 1, pt: 1, alignItems: 'center' }}>
          <Button
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}
            disabled={removing}
          >
            {t('detail.removeService')}
          </Button>
          <Box sx={{ flex: 1 }} />
          {updateDetails.isPending ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
              <CircularProgress size={14} />
              <Typography variant="caption">{t('detail.autosaveSaving')}</Typography>
            </Box>
          ) : updateDetails.isError || localSaveError ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <ErrorOutlineIcon fontSize="small" color="error" />
              <Typography variant="caption" color="error">
                {conflictBlocked ? t('detail.autosaveConflict') : (saveErrorText ?? t('detail.detailsSaveFailed'))}
              </Typography>
              <Button size="small" onClick={() => {
                if (!conflictBlocked) { void autosave(); return; }
                const incoming = incomingBaseRef.current;
                const source = incoming && (!baseRef.current || Date.parse(incoming.updatedAt) >= Date.parse(baseRef.current.updatedAt)) ? incoming : baseRef.current;
                if (source) {
                  baseRef.current = source; const data = serviceDetailsPayload(source.service);
                  setNotes(data.notes ?? null); setLaborItems(prev => mergeLaborItemDrafts(prev, source.service.laborItems));
                  setParts(toPartDrafts(data.parts)); writePricing(current => remapRowPricing(current, undefined, data.parts ?? [])); setExtras(data.extras ?? []);
                  setPriceInputs((data.parts ?? []).map(row => (row.unitPriceGrossCents / 100).toFixed(2)));
                  setQuantityInputs((data.parts ?? []).map(row => String(row.quantity)));
                  setExtraPriceInputs((data.extras ?? []).map(row => (row.amountGrossCents / 100).toFixed(2)));
                  setExtraQuantityInputs((data.extras ?? []).map(row => row.quantity == null ? '' : String(row.quantity)));
                }
                conflictBlockedRef.current = false; setConflictBlocked(false); setLocalSaveError(false); setSaveErrorText(null);
                unsentBlankRowsRef.current = false;
                updateDetails.reset(); setIsDirty(false); setReloadGeneration(n => n + 1); onSaved?.();
              }}>
                {t(conflictBlocked ? 'detail.autosaveDiscardReload' : 'detail.autosaveRetry')}
              </Button>
            </Box>
          ) : isDirty ? (
            <Typography variant="caption" color="text.secondary">
              {t('detail.autosaveUnsaved')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
              <CheckCircleIcon fontSize="small" />
              <Typography variant="caption">{t('detail.autosaveSaved')}</Typography>
            </Box>
          )}
        </Box>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        title={t('detail.removeService')}
        message={t('detail.removeServiceConfirm')}
        confirmColor="error"
        onConfirm={() => {
          setConfirmOpen(false);
          onRemove(bookingServiceId);
        }}
        onCancel={() => setConfirmOpen(false)}
        loading={removing}
      />
      <CertificateOverrideDialog
        open={certificateGate !== null}
        submitting={overrideSubmitting}
        blocked={certificateGate?.blocked ?? []}
        onClose={handleCertificateOverrideClose}
        onSubmit={handleCertificateOverrideSubmit}
      />
    </>
  );
}
